import ModalBig from "components/common/modalBig";
import {useTranslation} from "react-i18next";
import React, {useState, useEffect} from "react";
import {FormProvider, useForm} from "react-hook-form";

import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import { Delete as DeleteIcon } from '@vedmarket/ui-kit';
import { Check as CheckIcon } from '@vedmarket/ui-kit';

import DateInputSupplier from "./dateInputSupplier";
import PhoneInput, {CountryData} from "react-phone-input-2";
import ru from "react-phone-input-2/lang/ru.json";
import {checkInn, checkKpp, checkOgrn,
  RUSSIA_COUNTRY_NAME,
  RUSSIA_PHONE_NUMBER_REGEXP
} from "../../../../../../../../../../../utils/validation";
import {COLORS} from "../../../../../../../../../../../config/constants";

import {
  useCreateSupplierMutation, useUpdateSupplierMutation
} from "../../../../../../../../../../../api/dictionary";
import Notification, {NOTIFICATION_TYPE} from "../../../../../../../../../../common/notification";

import './supplierModal.scss';

enum MODAL_ADD_FORM {
    EMAIL = 'email',
    PHONE = 'phone',
}

export const enum SUPPLIER_FORM {
    NAME = 'name',
    CONTRACT_NUM = 'contract_num',
    CONTRACT_DATE = 'contract_date',
    LEGAL_ADDRESS = 'legal_address',
    PHONE = 'phone',
    CONTACT_PERSON = 'contact_person',
    INN = 'inn',
    KPP = 'kpp',
    OGRN = 'ogrn',
    ADDITIONAL_INFO = 'additional_info',
}

export type SupplierForm = {
    [SUPPLIER_FORM.NAME]: string
    [SUPPLIER_FORM.CONTRACT_NUM]: string
    [SUPPLIER_FORM.CONTRACT_DATE]?: string
    [SUPPLIER_FORM.LEGAL_ADDRESS]: string
    [SUPPLIER_FORM.PHONE]: string
    [SUPPLIER_FORM.CONTACT_PERSON]: string
    [SUPPLIER_FORM.INN]: string
    [SUPPLIER_FORM.KPP]: string
    [SUPPLIER_FORM.OGRN]: string
    [SUPPLIER_FORM.ADDITIONAL_INFO]: string
}

const ModalSupplier = ({ insideModal, isEdit, isOpen, handleClose, onAdd, currentSupplier, onUpdate } : SupplierModalProps ) => {

  const { t } = useTranslation()

  const [ errorMessage, setErrorMessage ] = useState(false);

  const [ createSupplier ] = useCreateSupplierMutation()
  const [ updateSupplier ] = useUpdateSupplierMutation()

  const methods = useForm<SupplierForm>({
    mode: 'onBlur',
    reValidateMode: 'onBlur'
  })

  const resetFields: SupplierForm = {
    [SUPPLIER_FORM.NAME]: '',
    [SUPPLIER_FORM.CONTRACT_NUM]: '',
    [SUPPLIER_FORM.CONTRACT_DATE]: undefined,
    [SUPPLIER_FORM.LEGAL_ADDRESS]: '',
    [SUPPLIER_FORM.PHONE]: '',
    [SUPPLIER_FORM.CONTACT_PERSON]: '',
    [SUPPLIER_FORM.INN]: '',
    [SUPPLIER_FORM.KPP]: '',
    [SUPPLIER_FORM.OGRN]: '',
    [SUPPLIER_FORM.ADDITIONAL_INFO]: '',
  };

  const { register, reset, getValues, handleSubmit, setValue, clearErrors, setError, formState: { errors, isValid } } = methods

  useEffect(() => {
    if (!isOpen) return;

    reset(isEdit && currentSupplier ? currentSupplier : resetFields);
  }, [ isOpen, isEdit, currentSupplier, reset ]);

  // @ts-ignore
  const handlePhoneChange = (value: string, country, e, formattedValue: string) => {
    setValue(SUPPLIER_FORM.PHONE, formattedValue)
  }

  const handlePhoneBlur = (event: React.FocusEvent<HTMLInputElement>, data: CountryData) => {
    let {value} = event.target;
    value = value.replace(/[+()-/ ]/g, "");

    if (data.name === RUSSIA_COUNTRY_NAME) {
      value.match(RUSSIA_PHONE_NUMBER_REGEXP) ? clearErrors(SUPPLIER_FORM.PHONE) : setError(SUPPLIER_FORM.PHONE, { type: "custom",  message: t('errors__message__incorrectPhoneError') });
    }
  }

  const onSubmit = handleSubmit (async (values) => {

    try {
      if (isEdit && currentSupplier) {
        await updateSupplier({
          id: currentSupplier.id,
          ...values
        }).unwrap();

        onUpdate();
        reset();
        return;
      }

      const response = await createSupplier({ ...values }).unwrap();

      onAdd(response);
      reset();
    } catch (error) {
      // @ts-ignore
      setErrorMessage(true);
    }
  })

  const errorInn = t('errors__message__incorrectValueInn');
  const errorKpp = t('errors__message__incorrectValueKpp');
  const errorOgrn = t('errors__message__incorrectValueOrgn');

  const onClose = () =>{
    handleClose();
    reset();
  }

  return (
    <ModalBig
      isOpen={isOpen}
      onClose={onClose}
      insideModal={insideModal}
      title={isEdit ? t('calc__text__main__editSupplierCompany') : t('calc__text__main__addSupplierCompany')}
      width={784}
    >
      <FormProvider {...methods}>
        <div className='supplier-modal'>
          <TextField
            {...register(SUPPLIER_FORM.NAME, { required: true }) }
            error={Boolean(errors[SUPPLIER_FORM.NAME])}
            helperText={errors[SUPPLIER_FORM.NAME]?.message}
            variant='outlined'
            label={t('common__text__name')}
            className={`supplier-modal__${SUPPLIER_FORM.NAME}`}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            {...register(SUPPLIER_FORM.CONTRACT_NUM) }
            error={Boolean(errors[SUPPLIER_FORM.CONTRACT_NUM])}
            helperText={errors[SUPPLIER_FORM.CONTRACT_NUM]?.message}
            variant='outlined'
            label={t('calc__text__main__contractNumberNotRequired')}
            className={`supplier-modal__${SUPPLIER_FORM.CONTRACT_NUM}`}
            InputLabelProps={{ shrink: true }}
          />
          <DateInputSupplier/>
          <TextField
            {...register(SUPPLIER_FORM.LEGAL_ADDRESS) }
            error={Boolean(errors[SUPPLIER_FORM.LEGAL_ADDRESS])}
            helperText={errors[SUPPLIER_FORM.LEGAL_ADDRESS]?.message}
            variant='outlined'
            label={t('calc__text__main__addressNotRequired')}
            className={`supplier-modal__${SUPPLIER_FORM.LEGAL_ADDRESS}`}
            InputLabelProps={{ shrink: true }}
          />
          <div className='supplier-modal__phone'>
            <PhoneInput
              country={'ru'}
              value={getValues(SUPPLIER_FORM.PHONE)}
              onChange={handlePhoneChange}
              onBlur={handlePhoneBlur}
              // @ts-ignore
              isValid={!errors[SUPPLIER_FORM.PHONE]}
              localization={ru}
              specialLabel={t('common__text__phoneNotRequired')}
              inputClass='supplier-modal__phone-input'
            />
            <span className='supplier-modal__phone-error'>{errors[MODAL_ADD_FORM.PHONE]?.message}</span>
          </div>
          <TextField
            {...register(SUPPLIER_FORM.INN, {
              validate: (value) => checkInn(value, errorInn),
            })}
            error={Boolean(errors[SUPPLIER_FORM.INN])}
            helperText={errors[SUPPLIER_FORM.INN]?.message}
            variant='outlined'
            label={t('calc__text__main__innNotRequired')}
            className={`supplier-modal__${SUPPLIER_FORM.INN}`}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            {...register(SUPPLIER_FORM.KPP, {
              validate: (value) => checkKpp(value, errorKpp),
            }) }
            error={Boolean(errors[SUPPLIER_FORM.KPP])}
            helperText={errors[SUPPLIER_FORM.KPP]?.message}
            variant='outlined'
            label={t('calc__text__main__kppNotRequired')}
            className={`supplier-modal__${SUPPLIER_FORM.KPP}`}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            {...register(SUPPLIER_FORM.OGRN, {
              validate: (value) => checkOgrn(value, errorOgrn),
            }) }
            error={Boolean(errors[SUPPLIER_FORM.OGRN])}
            helperText={errors[SUPPLIER_FORM.OGRN]?.message}
            variant='outlined'
            label={t('calc__text__main__ogrnNotRequired')}
            className={`supplier-modal__${SUPPLIER_FORM.OGRN}`}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            {...register(SUPPLIER_FORM.CONTACT_PERSON) }
            error={Boolean(errors[SUPPLIER_FORM.CONTACT_PERSON])}
            helperText={errors[SUPPLIER_FORM.CONTACT_PERSON]?.message}
            variant='outlined'
            label={t('calc__text__main__fullNameOfTheContactPersonNotRequired')}
            className={`supplier-modal__${SUPPLIER_FORM.CONTACT_PERSON}`}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            {...register(SUPPLIER_FORM.ADDITIONAL_INFO) }
            variant='outlined'
            label={t('calc__text__main__commentsNotRequired')}
            className={`supplier-modal__${SUPPLIER_FORM.ADDITIONAL_INFO}`}
            InputLabelProps={{ shrink: true }}
            multiline
            minRows={2}
          />
        </div>
        <hr className='supplier-modal__line'/>
        <div className='supplier-modal__actions'>
          {isEdit && <IconButton>
            <DeleteIcon style={{
              color: COLORS.LIGHT_ACTION
            }}/>
          </IconButton>}
          <Button
            variant='outlined'
            onClick={handleClose}
            sx={{ width: isEdit ? '308px' : '332px' }}
          >{t('actions__text__cancel')}</Button>
          <Button
            variant='contained'
            onClick={onSubmit}
            startIcon={<CheckIcon style={{ width: '22px', height: '22px' }} />}
            sx={{ width: isEdit ? '308px' : '332px' }}
            disabled={!isValid}
          >{t('actions__text__saveChanges')}</Button>
        </div>
      </FormProvider>
      {errorMessage && <Notification type={NOTIFICATION_TYPE.ERROR} title={t('common__text__error')} />}
    </ModalBig>
  )
}

export default ModalSupplier
